// EcosystemProfileDetail — single-specialist detail page.
//
// Wired to the route /ecosystem/:profileId. Previously the marketplace card
// click navigated here without the route existing, which dumped the user
// back at the project list (Roger's "jumps back to the project page"
// report on 23-May).
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, AlertCircle, Loader2, MapPin, Star, ShieldCheck, UserPlus } from 'lucide-react'
import { getEcosystemProfile } from '../../services/api'
import type { EcosystemProfile } from '../../types'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'
import ContextualTip from '../../components/ui/ContextualTip'
import HelpPopover from '../../components/help/HelpPopover'

export default function EcosystemProfileDetail() {
  const { profileId } = useParams<{ profileId: string }>()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<EcosystemProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!profileId) return
    setLoading(true)
    setError(null)
    getEcosystemProfile(profileId)
      .then(setProfile)
      .catch(() => setError('Specialist not found.'))
      .finally(() => setLoading(false))
  }, [profileId])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading specialist…
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/ecosystem')}
          className="flex items-center text-sm text-iflow-600 hover:underline mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Ecosystem
        </button>
        <EmptyState
          icon={AlertCircle}
          title="Specialist not found"
          description={error ?? 'This profile may have been removed or is no longer available.'}
          action={
            <button
              onClick={() => navigate('/ecosystem')}
              className="px-5 py-2.5 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
            >
              Browse the Ecosystem
            </button>
          }
        />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/ecosystem')}
        className="flex items-center text-sm text-iflow-600 hover:underline mb-4"
        data-testid="ecosystem-profile-back"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Ecosystem
      </button>

      <PageHeader
        title={profile.name}
        description={profile.headline}
        helpKey="ecosystem"
        status={profile.verified ? { label: 'Verified', tone: 'success' } : undefined}
        primaryAction={
          // P7 — no fake success. There is no direct connect API from a profile
          // page; connections are initiated through blind matching. Render the
          // control honestly disabled with the real path in its title.
          <button
            type="button"
            disabled
            title="Connections are initiated through blind matching. Open this specialist from a project match to request contact."
            className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-400"
          >
            <UserPlus className="w-4 h-4" /> Connect
          </button>
        }
      />

      <ContextualTip id="ecosystem-connect-path" tone="info">
        Ready to collaborate? Connections happen through blind matching, not a
        direct request. Open one of your projects, review its matches, and send a
        connection request from there — the platform keeps both sides’ detail
        private until you both agree to reveal more.{' '}
        <HelpPopover topic="concept-blind-matching" size={14} />
      </ContextualTip>

      <div className="mt-4 bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-iflow-100 flex items-center justify-center text-xl font-bold text-iflow-700 shrink-0">
            {profile.name?.[0] ?? '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {profile.primary_role && (
                <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {profile.primary_role}
                </span>
              )}
              {profile.verified && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
              {typeof profile.avg_rating === 'number' && profile.avg_rating > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  {profile.avg_rating.toFixed(1)}
                </span>
              )}
              {profile.availability && (
                <span className="flex items-center gap-1 text-gray-500">
                  <MapPin className="w-3 h-3" /> {profile.availability}
                </span>
              )}
            </div>
          </div>
        </div>

        {profile.bio && (
          <section className="mt-6">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">About</h2>
            <p className="text-sm text-gray-700 whitespace-pre-line">{profile.bio}</p>
          </section>
        )}

        {profile.skills && profile.skills.length > 0 && (
          <section className="mt-5">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Skills</h2>
            <div className="flex flex-wrap gap-1.5">
              {profile.skills.map((s) => (
                <span key={s} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">{s}</span>
              ))}
            </div>
          </section>
        )}

        {profile.domains && profile.domains.length > 0 && (
          <section className="mt-5">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Domains</h2>
            <div className="flex flex-wrap gap-1.5">
              {profile.domains.map((d) => (
                <span key={d} className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-xs">{d}</span>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
